import { useState, useEffect, useRef, type ReactNode } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import type { LivePresenceEntry } from "@cs2/contracts";
import type { Session } from "@cs2/contracts/transport";
import {
  Crosshair,
  Pencil,
  Library,
  Users,
  History,
  LogOut,
  Menu,
  X,
  UserRound,
  ChevronDown,
  CircleHelp,
} from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { OnboardingDialog } from "../onboarding/OnboardingDialog.js";
import { useOnboarding } from "../onboarding/useOnboarding.js";
import "./app-shell.css";

type Section = "quickview" | "editor" | "catalog";
type Props = {
  active: Section;
  session?: Session | null;
  onMembers?: () => void;
  online?: readonly LivePresenceEntry[];
  presenceConnected?: boolean;
  onSnapshots?: (() => void) | undefined;
  onSignOut?: () => void;
  signingOut?: boolean;
  beforeNavigate?: () => Promise<boolean>;
  /** Tour für die aktive Seite; Lade-/Fehlerzustände schalten sie ab. */
  onboarding?: boolean;
  /** Blendet die Navigationsleiste aus, z. B. im Ingame-Modus des Katalogs. */
  headerHidden?: boolean;
  children: ReactNode;
};

export function Brand() {
  return (
    <span className="app-brand">
      <Crosshair aria-hidden="true" />
      <span>
        CS2<span> TACTICS</span>
      </span>
    </span>
  );
}

/** Shared presentation only: page owners retain their session and dialog actions. */
export function AppShell({
  active,
  session,
  onMembers,
  online = [],
  presenceConnected = false,
  onSnapshots,
  onSignOut,
  signingOut,
  beforeNavigate,
  onboarding = true,
  headerHidden = false,
  children,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const tour = useOnboarding(session, active, onboarding);
  const navigate = useNavigate();
  const location = useLocation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const pendingNavigation = useRef(false);
  useEffect(() => {
    if (!menuOpen) return;
    function close(event: KeyboardEvent | PointerEvent) {
      if (event instanceof KeyboardEvent && event.key === "Escape") {
        setMenuOpen(false);
        triggerRef.current?.focus();
      } else if (
        !(event instanceof KeyboardEvent) &&
        !menuRef.current?.contains(event.target as Node)
      )
        setMenuOpen(false);
    }
    window.addEventListener("keydown", close);
    window.addEventListener("pointerdown", close);
    return () => {
      window.removeEventListener("keydown", close);
      window.removeEventListener("pointerdown", close);
    };
  }, [menuOpen]);
  async function handleNavigation(
    event: ReactMouseEvent<HTMLAnchorElement>,
    to: string,
  ) {
    setMenuOpen(false);
    if (
      !beforeNavigate ||
      to === location.pathname ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    event.preventDefault();
    if (pendingNavigation.current) return;
    pendingNavigation.current = true;
    try {
      if (await beforeNavigate()) void navigate(to);
    } finally {
      pendingNavigation.current = false;
    }
  }
  function toggleMenu(event: ReactMouseEvent<HTMLButtonElement>) {
    triggerRef.current = event.currentTarget;
    setMenuOpen(!menuOpen);
  }
  function closeForAction() {
    setMenuOpen(false);
    if (menuOpen) triggerRef.current?.focus();
  }
  const selfId = session?.user?.id;
  const onlineCount = presenceConnected
    ? new Set([
        ...online.map((member) => member.userId),
        ...(selfId ? [selfId] : []),
      ]).size
    : null;
  const teamLabel = `Mitglieder öffnen · ${onlineCount === null ? "Online-Status nicht verfügbar" : onlineCount === 1 ? "1 Mitglied online" : `${onlineCount} Mitglieder online`}`;
  const authenticated = session?.authenticated === true;
  const canEdit =
    session?.user?.role === "captain" || session?.user?.role === "editor";
  const items = [
    { id: "quickview", to: "/", label: "Quickview", Icon: Crosshair },
    ...(canEdit
      ? [{ id: "editor", to: "/editor", label: "Taktik-Editor", Icon: Pencil }]
      : []),
    ...(authenticated
      ? [
          {
            id: "catalog",
            to: "/granaten",
            label: "Granatenkatalog",
            Icon: Library,
          },
        ]
      : []),
  ];
  const links = items.map(({ id, to, label, Icon }) => (
    <NavLink
      key={id}
      to={to}
      end
      className={id === active ? "selected" : ""}
      onClick={(event) => void handleNavigation(event, to)}
    >
      <Icon aria-hidden="true" />
      <span>{label}</span>
    </NavLink>
  ));
  const actions = (
    <>
      {onMembers && authenticated ? (
        <button
          onClick={() => {
            closeForAction();
            onMembers();
          }}
        >
          <Users aria-hidden="true" />
          <span>Mitglieder</span>
        </button>
      ) : null}
      {onSnapshots && authenticated && canEdit ? (
        <button
          onClick={() => {
            closeForAction();
            onSnapshots();
          }}
        >
          <History aria-hidden="true" />
          <span>Veröffentlichungen</span>
        </button>
      ) : null}
      {tour.available ? (
        <button
          onClick={() => {
            closeForAction();
            tour.openTour();
          }}
        >
          <CircleHelp aria-hidden="true" />
          <span>Tour &amp; Hilfe</span>
        </button>
      ) : null}
    </>
  );
  return (
    <div
      className={`app-shell${headerHidden ? " app-shell--header-hidden" : ""}`}
    >
      <div className="app-shell-content">
        <header className="app-header" ref={menuRef}>
          <NavLink
            to="/"
            className="app-brand-link"
            aria-label="CS2 Tactics – Quickview"
            onClick={(event) => void handleNavigation(event, "/")}
          >
            <Brand />
          </NavLink>
          <nav aria-label="Hauptnavigation">{links}</nav>
          <div className="app-account">
            {authenticated ? (
              <>
                <button
                  className="app-account-team"
                  aria-label={teamLabel}
                  title={teamLabel}
                  disabled={!onMembers}
                  onClick={(event) => {
                    triggerRef.current = event.currentTarget;
                    closeForAction();
                    onMembers?.();
                  }}
                >
                  <Users aria-hidden="true" />
                  <span className="app-account-team-label">Mein Team</span>
                  <span
                    className={`app-online-dot${onlineCount !== null && onlineCount > 0 ? " is-online" : ""}`}
                    aria-hidden="true"
                  />
                  <span className="app-online-count" aria-hidden="true">
                    {onlineCount ?? "–"}
                  </span>
                </button>
                <span className="app-role">{session.user?.role}</span>
              </>
            ) : (
              <NavLink to="/editor/login">Anmelden</NavLink>
            )}
            {tour.available ? (
              <button
                className="app-account-help"
                aria-label="Tour & Hilfe"
                title="Tour & Hilfe"
                onClick={() => {
                  closeForAction();
                  tour.openTour();
                }}
              >
                <CircleHelp aria-hidden="true" />
              </button>
            ) : null}
            <button
              className="app-account-toggle"
              aria-label="Kontomenü"
              aria-expanded={menuOpen}
              aria-controls="app-account-menu"
              onClick={toggleMenu}
            >
              <span className="app-avatar">
                <UserRound aria-hidden="true" />
              </span>
              <ChevronDown aria-hidden="true" />
            </button>
          </div>
          <button
            className="app-menu-toggle"
            aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={menuOpen}
            aria-controls="app-account-menu"
            onClick={toggleMenu}
          >
            {menuOpen ? <X /> : <Menu />}
          </button>
          {menuOpen ? (
            <div className="app-menu" id="app-account-menu">
              <button
                className="app-menu-dismiss"
                onClick={() => {
                  setMenuOpen(false);
                  triggerRef.current?.focus();
                }}
                aria-label="Menü schließen"
              >
                <X />
              </button>
              {session?.user?.email ? (
                <small>{session.user.email}</small>
              ) : null}
              <nav aria-label="Kontomenü">
                {links}
                {actions}
                {authenticated && onSignOut ? (
                  <button onClick={onSignOut} disabled={signingOut}>
                    <LogOut />
                    Abmelden
                  </button>
                ) : (
                  <NavLink to="/editor/login">Anmelden</NavLink>
                )}
              </nav>
            </div>
          ) : null}
        </header>
        <div className="app-page">{children}</div>
        <nav
          className="app-mobile-navigation"
          aria-label="Mobile Hauptnavigation"
        >
          {links}
        </nav>
      </div>
      <OnboardingDialog tour={tour} />
    </div>
  );
}
